import { useEffect, useRef, useState } from 'react';
import type { ThinkingBlock } from '../stores/appStore';

interface ThinkingIndicatorProps {
  isThinking: boolean;
  content?: string | null;
  blocks?: ThinkingBlock[];
  isCommitted?: boolean;
  title?: string;
}

export default function ThinkingIndicator({
  isThinking,
  content,
  blocks,
  isCommitted,
  title,
}: ThinkingIndicatorProps) {
  const [expanded, setExpanded] = useState(!isCommitted);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const prevThinkingRef = useRef(isThinking);

  // Auto-expand when thinking starts; stay expanded through tool calls
  // until the message is committed to history (which re-mounts collapsed).
  useEffect(() => {
    if (!isCommitted) {
      if (!prevThinkingRef.current && isThinking) {
        setExpanded(true);
      }
    }
    prevThinkingRef.current = isThinking;
  }, [isThinking, isCommitted]);

  // Auto-scroll the thinking content container while streaming
  useEffect(() => {
    if (expanded && contentRef.current && !isCommitted) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [content, expanded, isCommitted]);

  // Combine blocks into a single display string for committed messages
  const blockText = blocks?.map((b) => b.thinking).join('\n\n') ?? null;
  const displayText = content || blockText;

  const activeTitle = title ?? '// Thinking';
  const committedTitle = title ?? '// Thought process';

  // Mode 1: Committed message with thinking blocks — collapsed "Thought process" toggle
  if (isCommitted && displayText) {
    return (
      <div className="mt-1">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 transition-colors"
          style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--theme-muted-foreground)' }}
        >
          <span style={{ fontSize: '0.55rem' }}>{expanded ? '▼' : '▶'}</span>
          <span>{committedTitle}</span>
        </button>
        {expanded && (
          <div
            ref={contentRef}
            className="thinking-content mt-1.5 max-h-48 overflow-y-auto rounded-lg px-3 py-2 text-xs"
            style={{ border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(37, 42, 64, 0.5)' }}
          >
            {displayText}
          </div>
        )}
      </div>
    );
  }

  // Mode 2: Streaming with content — collapsible, auto-expanded
  if (isThinking && displayText) {
    return (
      <div className="max-w-full rounded-lg px-4 py-2 text-sm" style={{ border: '1px solid rgba(255,255,255,0.06)', background: 'var(--theme-secondary)', borderLeft: '2px solid rgba(255, 123, 53, 0.3)' }}>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 w-full"
        >
          <span className="thinking-pulse" style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--theme-muted-foreground)' }}>{activeTitle}</span>
          <span className="ml-auto" style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.55rem', color: 'var(--theme-muted-foreground)' }}>
            {expanded ? '▼' : '▶'}
          </span>
        </button>
        {expanded && (
          <div
            ref={contentRef}
            className="thinking-content mt-2 max-h-48 overflow-y-auto rounded-lg px-3 py-2 text-xs"
            style={{ border: '1px solid rgba(255,255,255,0.04)', background: 'rgba(11, 13, 20, 0.5)' }}
          >
            {displayText}
          </div>
        )}
      </div>
    );
  }

  // Mode 3: Simple pulsing indicator (no content, or v2.9 compat)
  if (isThinking) {
    return (
      <div className="max-w-full rounded-lg px-4 py-2 text-sm" style={{ border: '1px solid rgba(255,255,255,0.06)', background: 'var(--theme-secondary)', borderLeft: '2px solid rgba(255, 123, 53, 0.3)' }}>
        <span className="thinking-pulse" style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--theme-muted-foreground)' }}>{activeTitle}</span>
      </div>
    );
  }

  // Mode 4: Thinking finished but still in live area — show collapsed toggle
  if (!isCommitted && displayText) {
    return (
      <div className="mt-1">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 transition-colors"
          style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--theme-muted-foreground)' }}
        >
          <span style={{ fontSize: '0.55rem' }}>{expanded ? '▼' : '▶'}</span>
          <span>{committedTitle}</span>
        </button>
        {expanded && (
          <div
            ref={contentRef}
            className="thinking-content mt-1.5 max-h-48 overflow-y-auto rounded-lg px-3 py-2 text-xs"
            style={{ border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(37, 42, 64, 0.5)' }}
          >
            {displayText}
          </div>
        )}
      </div>
    );
  }

  return null;
}
